import React from 'react';
import {
  formatCurrency,
  getPaymentTypeIcon,
  getPaymentTypeColor
} from './kasaBankHelpers';

/**
 * "Płatności" tab content - payment breakdown by type.
 * Shows TODAY's data first, then MONTHLY data below.
 */
function PaymentsTab({ paymentsByType, dailySummary, monthlyStats }) {
  if (!paymentsByType) return null;

  const paymentTypes = Object.entries(paymentsByType);

  return (
    <div>
      {/* Sekcja DZIŚ */}
      <div style={{ marginBottom: '1.5rem' }}>
        <h6 style={{
          margin: '0 0 0.75rem 0',
          fontWeight: '600',
          color: 'var(--primary)',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <i className="fas fa-calendar-day"></i>
          Dzisiaj
        </h6>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '0.5rem'
        }}>
          {paymentTypes.map(([typ, dane]) => (
            <div key={`today-${typ}`} style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${getPaymentTypeColor(typ)}`,
              borderRadius: '4px',
              backgroundColor: 'var(--card-bg)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                marginBottom: '0.4rem'
              }}>
                <i className={getPaymentTypeIcon(typ)} style={{
                  color: getPaymentTypeColor(typ),
                  fontSize: '0.7rem'
                }}></i>
                <span style={{
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  textTransform: 'capitalize',
                  color: 'var(--text)'
                }}>{typ}</span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '0.3rem',
                  backgroundColor: 'var(--success-light)',
                  borderRadius: '3px'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', color: 'var(--success)' }}>
                    +{formatCurrency(dane.kp?.suma_dzis || 0)}
                  </div>
                  <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>KP</div>
                </div>
                <div style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '0.3rem',
                  backgroundColor: 'var(--danger-light)',
                  borderRadius: '3px'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', color: 'var(--danger)' }}>
                    -{formatCurrency(dane.kw?.suma_dzis || 0)}
                  </div>
                  <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>KW</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Podsumowanie dzienne */}
        {dailySummary && (
          <div style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--bg)',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-around',
            fontSize: '0.75rem'
          }}>
            <span>
              <span style={{ color: 'var(--text-muted)' }}>Wpływy: </span>
              <span style={{ fontWeight: '600', color: 'var(--success)' }}>
                {formatCurrency(dailySummary.total_in || 0)}
              </span>
            </span>
            <span>
              <span style={{ color: 'var(--text-muted)' }}>Wydatki: </span>
              <span style={{ fontWeight: '600', color: 'var(--danger)' }}>
                {formatCurrency(dailySummary.total_out || 0)}
              </span>
            </span>
            <span>
              <span style={{ color: 'var(--text-muted)' }}>Bilans: </span>
              <span style={{
                fontWeight: '600',
                color: (dailySummary.balance || 0) >= 0 ? 'var(--success)' : 'var(--danger)'
              }}>
                {formatCurrency(dailySummary.balance || 0)}
              </span>
            </span>
          </div>
        )}
      </div>

      {/* Sekcja MIESIĄC */}
      <div>
        <h6 style={{
          margin: '0 0 0.75rem 0',
          fontWeight: '600',
          color: 'var(--warning)',
          fontSize: '0.85rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem'
        }}>
          <i className="fas fa-calendar-alt"></i>
          Cały miesiąc
        </h6>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '0.5rem'
        }}>
          {paymentTypes.map(([typ, dane]) => (
            <div key={`month-${typ}`} style={{
              padding: '0.6rem 0.75rem',
              border: '1px solid var(--border)',
              borderLeft: `3px solid ${getPaymentTypeColor(typ)}`,
              borderRadius: '4px',
              backgroundColor: 'var(--card-bg)'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                marginBottom: '0.4rem'
              }}>
                <i className={getPaymentTypeIcon(typ)} style={{
                  color: getPaymentTypeColor(typ),
                  fontSize: '0.7rem'
                }}></i>
                <span style={{
                  fontWeight: '600',
                  fontSize: '0.75rem',
                  textTransform: 'capitalize',
                  color: 'var(--text)'
                }}>{typ}</span>
                <span style={{
                  marginLeft: 'auto',
                  fontSize: '0.65rem',
                  color: 'var(--text-muted)'
                }}>
                  Saldo: {formatCurrency(dane.saldo || 0)}
                </span>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '0.3rem',
                  backgroundColor: 'var(--success-light)',
                  borderRadius: '3px'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', color: 'var(--success)' }}>
                    +{formatCurrency(dane.kp?.suma || 0)}
                  </div>
                  <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                    KP ({dane.kp?.liczba || 0})
                  </div>
                </div>
                <div style={{
                  flex: 1,
                  textAlign: 'center',
                  padding: '0.3rem',
                  backgroundColor: 'var(--danger-light)',
                  borderRadius: '3px'
                }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: '600', color: 'var(--danger)' }}>
                    -{formatCurrency(dane.kw?.suma || 0)}
                  </div>
                  <div style={{ fontSize: '0.6rem', color: 'var(--text-muted)' }}>
                    KW ({dane.kw?.liczba || 0})
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Podsumowanie miesięczne */}
        {monthlyStats && (
          <div style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.75rem',
            backgroundColor: 'var(--bg)',
            borderRadius: '4px',
            display: 'flex',
            justifyContent: 'space-around',
            fontSize: '0.75rem'
          }}>
            <span>
              <span style={{ color: 'var(--text-muted)' }}>Transakcji: </span>
              <span style={{ fontWeight: '600', color: 'var(--info)' }}>
                {monthlyStats.total_transactions || 0}
              </span>
            </span>
            <span>
              <span style={{ color: 'var(--text-muted)' }}>Obrót: </span>
              <span style={{ fontWeight: '600', color: 'var(--success)' }}>
                {formatCurrency(monthlyStats.total_amount || 0)}
              </span>
            </span>
            <span>
              <span style={{ color: 'var(--text-muted)' }}>Średnia: </span>
              <span style={{ fontWeight: '600', color: 'var(--info)' }}>
                {formatCurrency(monthlyStats.avg_transaction || 0)}
              </span>
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

export default PaymentsTab;
